import { CronExpressionParser } from 'cron-parser/dist/CronExpressionParser';
import { DateTime } from 'luxon';
import { PersistedWorkflow } from '@cotera/sdk/core';

export const DAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

export type IntervalType = 'hourly' | 'daily' | 'weekly' | 'custom';

export type ParsedSchedule = {
  intervalType: IntervalType;
  hour: number;
  dayOfWeek: number;
};

export function formatHour(hour: number): string {
  if (hour === 0) {
    return '12:00 AM';
  }
  if (hour < 12) {
    return `${hour}:00 AM`;
  }
  if (hour === 12) {
    return '12:00 PM';
  }
  return `${hour - 12}:00 PM`;
}

function parseCronParts(cron: string): string[] {
  return cron.trim().split(/\s+/);
}

export function parseSchedule(cron: string): ParsedSchedule {
  const parts = parseCronParts(cron);
  if (parts.length !== 5) {
    return { intervalType: 'custom', hour: 9, dayOfWeek: 1 };
  }

  const [minute, hourStr, dayOfMonth, month, dowStr] = parts;
  const hour = parseInt(hourStr ?? '', 10);
  const dow = parseInt(dowStr ?? '', 10);

  if (minute !== '0' || dayOfMonth !== '*' || month !== '*') {
    return { intervalType: 'custom', hour: 9, dayOfWeek: 1 };
  }

  if (hourStr === '*' && dowStr === '*') {
    return { intervalType: 'hourly', hour: 9, dayOfWeek: 1 };
  }

  if (!Number.isNaN(hour) && dowStr === '*') {
    return { intervalType: 'daily', hour, dayOfWeek: 1 };
  }

  if (!Number.isNaN(hour) && !Number.isNaN(dow)) {
    return { intervalType: 'weekly', hour, dayOfWeek: dow };
  }

  return { intervalType: 'custom', hour: 9, dayOfWeek: 1 };
}

export function buildCron(
  type: IntervalType,
  hour: number,
  dayOfWeek: number
): string {
  switch (type) {
    case 'hourly':
      return '0 * * * *';
    case 'daily':
      return `0 ${hour} * * *`;
    case 'weekly':
      return `0 ${hour} * * ${dayOfWeek}`;
    case 'custom':
      return '';
  }
}

/**
 * Human-readable label for a cron schedule. Delegates to the shared SDK helper
 * ({@link PersistedWorkflow.cronScheduleLabel}) so this client fallback and the
 * server-derived/stored trigger labels use one implementation and never drift —
 * the SDK covers the same common shapes plus day-of-month and a cronstrue
 * fallback for genuinely custom expressions.
 */
export function humanReadableSchedule(cron: string): string {
  return PersistedWorkflow.cronScheduleLabel(cron);
}

export function getNextRun(cron: string): Date | null {
  try {
    const interval = CronExpressionParser.parse(cron, {
      currentDate: new Date(),
      tz: 'UTC',
    });
    return interval.next().toDate();
  } catch {
    return null;
  }
}

const MAX_RUNS_IN_RANGE = 1000;

export function getRunsInRange(cron: string, start: Date, end: Date): Date[] {
  try {
    const interval = CronExpressionParser.parse(cron, {
      currentDate: start,
      tz: 'UTC',
    });
    const runs: Date[] = [];
    for (let i = 0; i < MAX_RUNS_IN_RANGE; i++) {
      const next = interval.next().toDate();
      if (next.getTime() > end.getTime()) {
        break;
      }
      runs.push(next);
    }
    return runs;
  } catch {
    return [];
  }
}

export function formatNextRun(date: Date | null): string {
  if (date === null) {
    return '—';
  }
  return DateTime.fromJSDate(date).toRelative() ?? '—';
}
